import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Pagination, Typography } from '@mui/material';
import ChromaGrid from './ChromaGrid';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';

const MovieGrid = ({ movies, pagination, onPageChange }) => {
  const navigate = useNavigate();

  if (!movies || movies.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 12,
          px: 3,
          background: 'rgba(26, 26, 26, 0.4)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <MovieFilterIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          No movies found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your search or filters
        </Typography>
      </Box>
    );
  }

  // Transform movies to ChromaGrid format
  const chromaItems = movies.map(movie => {
    const formatYear = (date) => new Date(date).getFullYear();
    const getRatingColor = (rating) => {
      if (rating >= 9) return '#10B981'; // Green
      if (rating >= 8) return '#3B82F6'; // Blue
      if (rating >= 7) return '#F59E0B'; // Orange
      return '#EF4444'; // Red
    };

    const ratingColor = getRatingColor(movie.rating);

    return {
      id: movie._id,
      image: movie.posterUrl || 'https://via.placeholder.com/300x450?text=No+Image',
      title: movie.title,
      subtitle: movie.description?.substring(0, 80) + '...',
      handle: `⭐ ${movie.rating} • ${formatYear(movie.releaseDate)}`,
      borderColor: ratingColor,
      gradient: `linear-gradient(145deg, ${ratingColor}22, #000)`,
      description: `${movie.duration} min • ${movie.genre?.slice(0, 2).join(', ')}`
    };
  });

  const handleCardClick = (item) => {
    navigate(`/movie/${item.id}`);
  };

  return (
    <Box>
      <Box sx={{ minHeight: '600px', position: 'relative' }}>
        <ChromaGrid
          items={chromaItems}
          radius={300}
          damping={0.45}
          fadeOut={0.6}
          ease="power3.out"
          columns={3}
          onCardClick={handleCardClick}
        />
      </Box>

      {pagination && pagination.pages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 6,
            mb: 2,
          }}
        >
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={onPageChange}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'text.primary',
                fontWeight: 600,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.2)',
                  borderColor: '#6366f1',
                },
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  border: '1px solid #6366f1',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MovieGrid;
